import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { api, setAccessToken, setSessionExpiredHandler, setPlanLimitHandler } from "../lib/api";
import { adaptHolding, IDEAL_RANGES, applyRemoteSuggestionConfig } from "../lib/diveEngine";
import { captureDeepLink, screenAfterLogin, screenWhenLoggedOut } from "../lib/deepLink";

const DiveContext = createContext(null);
export const useDive = () => useContext(DiveContext);

// Must match backend/src/models/User.ts's plannerStateSchema defaults
// exactly — this is what a brand-new account (or any account whose
// plannerState was never saved) starts from, both here and on the server.
const DEFAULT_PLANNER_STATE = {
  mode: null, // null | "lumpsum" | "sip"
  lumpsumAmount: 50000,
  sipMonthly: 5000,
  sipStepUp: 10,
  sipYears: 10,
  sipExpandedMonthly: false,
};

export function DiveProvider({ children }) {
  const [screen, setScreenState] = useState("splash"); // onboarding + app screens
  // A real navigation STACK, not just a single "previous screen" slot — the
  // single-slot version broke as soon as a back-navigation itself became the
  // "previous" for the next one. Concretely: Home -> chooseMethod (previous
  // <- home) -> manualEntry (previous <- chooseMethod) -> back (goBack sets
  // screen to chooseMethod, but ALSO overwrites previous <- manualEntry,
  // since it's implemented as just another setScreen call) -> "Skip for now"
  // on chooseMethod now goes BACK to manualEntry instead of home, a 2-cycle
  // loop that never actually returns where the user started. A stack fixes
  // this the same way browser history does: every real navigation PUSHES
  // the screen being left, and goBack POPs — never pushes — so unwinding
  // repeatedly retraces the actual path taken, however deep, instead of
  // bouncing between the last two screens. Several screens (Ask DIVE, My
  // Holdings) are reachable from more than one place, which is exactly why
  // this needs to be a stack and not a fixed hardcoded destination either.
  const screenHistoryRef = useRef([]);
  const setScreen = useCallback((next) => {
    setScreenState((current) => {
      if (next !== current) screenHistoryRef.current.push(current);
      return next;
    });
  }, []);
  const goBack = useCallback(() => {
    setScreenState(() => screenHistoryRef.current.pop() ?? "home");
  }, []);

  // Closes whichever portfolio_edit/bot_scan "edit sessions" happen to be
  // open (usageService.ts::enforceEditSessionUsage) the moment the user
  // reaches Home — the signal the product wants for "one successful
  // portfolio update = one consumed limit," regardless of how many
  // individual mutations led up to it or which screens were visited along
  // the way (Manage Holdings -> Add Investments -> Home is one trip, one
  // unit; Add Investments -> Planner -> Home still counts, since this fires
  // on ARRIVING at Home, not on leaving the edit screen). Best-effort and a
  // no-op when nothing was open — the backend's own TTL is the real
  // fallback if this never fires. Deliberately keyed on `screen` alone
  // (every route back to Home already funnels through setScreen("home")),
  // not screen-specific wiring in each edit flow.
  useEffect(() => {
    if (screen !== "home") return;
    Promise.resolve(api.post("/usage/close-edit-sessions")).catch(() => undefined);
  }, [screen]);
  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState(null);
  // Read-only impersonation (Phase 7 of docs/ADMIN_PANEL_PLAN.md §5.1/§8) —
  // set only when this tab's session came from admin/screens/UserDetail.jsx's
  // "View as this user" action (sessionStorage.divve_impersonation), never
  // from a normal login. The backend itself is what actually blocks writes
  // (middleware/auth.ts's requireAuth) — this flag is purely for the banner.
  const [isImpersonating, setIsImpersonating] = useState(false);
  // Guided tour (components/Walkthrough.jsx) — open/close state lives here,
  // not locally in whichever component happens to render it, because three
  // independent places need to read or set it: DiveShell.jsx (auto-opens it
  // once, and its sidebar replay button), and Home.jsx (suppresses its own
  // GetStartedPopup while this is open, so the two never stack).
  const [walkthroughOpen, setWalkthroughOpen] = useState(false);

  const [holdings, setHoldings] = useState([]);
  const [holdingsLoading, setHoldingsLoading] = useState(false);
  // Distinguishes "the backend really has no holdings for this user" from "we
  // couldn't reach the backend" — without this, a network blip or expired
  // session makes loadHoldings() resetting to [] look identical to a
  // genuinely empty portfolio, and the UI would show the "add your first
  // holding" empty state over a real, non-empty portfolio.
  const [holdingsError, setHoldingsError] = useState(false);
  // Mirrors `holdings` so loadHoldings' catch block can return the
  // last-known-good list on failure without needing `holdings` itself in its
  // useCallback deps (which would recreate the callback — and re-run any
  // effect depending on it — on every successful load).
  const holdingsRef = useRef(holdings);
  useEffect(() => {
    holdingsRef.current = holdings;
  }, [holdings]);
  // The ONE canonical Dive Score (composite + apparent/real diversification +
  // full resilience breakdown), computed server-side. Home/X-Ray/Insights all
  // read this when no simulation is active, instead of each independently
  // recomputing a score client-side — there is one calculator, not two.
  const [scoreBreakdown, setScoreBreakdown] = useState(null);
  const loadScoreBreakdown = useCallback(async () => {
    try {
      const { data } = await api.get("/score/breakdown");
      setScoreBreakdown(data);
      return data;
    } catch (e) {
      setScoreBreakdown(null);
      return null;
    }
  }, []);
  const ranges = IDEAL_RANGES;
  const [prefs, setPrefs] = useState({
    risk: "Balanced", returnExpectation: "Moderate", diversificationPriority: "High",
    preferred: [], excluded: [],
  });
  const [askInstrument, setAskInstrument] = useState(null);
  // The holding ManualEntry should pre-fill and PATCH instead of POST when
  // it's opened for editing — same "selected item lives in context, target
  // screen reads it" pattern as askInstrument above. Cleared once the edit
  // is saved or abandoned so a later plain "add a holding" doesn't
  // accidentally reopen in edit mode.
  const [editingHolding, setEditingHolding] = useState(null);
  const [sims, setSims] = useState([]); // client-side "what-if" simulated allocations (not persisted per-user yet)

  // Divve Planner's inputs (mode + lumpsum amount + SIP fields) live here, not as
  // local useState inside Planner.jsx, because DiveShell's <AnimatePresence
  // key={screen}> unmounts a screen's component tree on every navigation away
  // from it — local state would silently reset to defaults each time the user
  // left and returned to the Planner tab. Lifting it here means it survives for
  // the rest of the session. Persisted server-side via PATCH /users/me/planner
  // (debounced below) so a returning user sees their real last-entered values,
  // not just the defaults — the demo phone frame and the logged-in app share
  // this same context instance without a remount between them, so on login/
  // signup this also has to explicitly load (or reset to defaults) the new
  // identity's own value; it's never automatic on its own.
  const [plannerState, setPlannerStateRaw] = useState(DEFAULT_PLANNER_STATE);
  const setPlannerState = useCallback((patch) => {
    setPlannerStateRaw((p) => ({ ...p, ...(typeof patch === "function" ? patch(p) : patch) }));
  }, []);
  const loadPlannerStateFrom = (u) => setPlannerStateRaw({ ...DEFAULT_PLANNER_STATE, ...(u?.plannerState || {}) });

  // Debounced save — Planner's slider/number inputs fire on every drag tick
  // or keystroke, and PATCHing on each one would spam the backend. Waits for
  // 800ms of no further changes before actually saving. `skipNextSaveRef`
  // suppresses the save that would otherwise fire right after loadPlannerStateFrom
  // itself changes plannerState (login/signup/session-restore) — that's a
  // load, not a user edit, and re-saving the identical value back is pure
  // waste. Best-effort: a failed save just gets superseded by the next real
  // edit, and a slider drag isn't worth interrupting with an error toast.
  const plannerSaveTimerRef = useRef(null);
  const skipNextPlannerSaveRef = useRef(true);
  useEffect(() => {
    if (skipNextPlannerSaveRef.current) {
      skipNextPlannerSaveRef.current = false;
      return undefined;
    }
    if (!user) return undefined;
    if (plannerSaveTimerRef.current) clearTimeout(plannerSaveTimerRef.current);
    plannerSaveTimerRef.current = setTimeout(() => {
      api.patch("/users/me/planner", plannerState).catch(() => {});
    }, 800);
    return () => clearTimeout(plannerSaveTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plannerState, user]);

  const addSim = (segment, amount) => {
    setSims((prev) => {
      const exists = prev.find((s) => s.segment === segment);
      return exists ? prev.map((s) => (s.segment === segment ? { ...s, amount } : s)) : [...prev, { segment, amount }];
    });
  };
  const resetSims = () => setSims([]);

  const loadHoldings = useCallback(async () => {
    setHoldingsLoading(true);
    setHoldingsError(false);
    try {
      const { data } = await api.get("/holdings");
      const adapted = (data.holdings || []).map(adaptHolding);
      setHoldings(adapted);
      loadScoreBreakdown(); // refresh the one canonical score alongside holdings — fire and forget
      return adapted;
    } catch (e) {
      // Deliberately does NOT clear holdings to [] — a transient failure
      // (network blip, expired session) must not make a real portfolio look
      // like it vanished. Keep whatever was last successfully loaded and let
      // the UI show a distinct "couldn't load" state instead (see Home.jsx).
      setHoldingsError(true);
      return holdingsRef.current;
    } finally {
      setHoldingsLoading(false);
    }
  }, [loadScoreBreakdown]);

  // On mount: pick up whatever Suggestion methodology config an admin has
  // published (docs/ADMIN_PANEL_PLAN.md Phase 2) — public, no auth needed,
  // independent of the session-restore effect below. Best-effort: on
  // failure (or before this resolves), diveEngine.js's bundled literal
  // constants stand in unchanged, exactly as if nothing had ever been
  // published. Mutates diveEngine.js's exports directly rather than storing
  // the result in React state — nothing here needs a re-render, just the
  // next natural read of those constants to see the new values.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/score/config");
        applyRemoteSuggestionConfig(data.suggestion);
      } catch (e) {
        // Stay on the bundled defaults — see the comment above.
      }
    })();
  }, []);

  // On mount: an impersonation hand-off (sessionStorage, set by
  // admin/screens/UserDetail.jsx just before opening this tab) always wins
  // over the normal cookie-based restore below — there is no refresh cookie
  // for this session at all, by design; it just expires naturally after 30
  // minutes (lib/api.js's own refresh attempt on that eventual 401 fails
  // with no cookie to use, which correctly falls through to the existing
  // "session expired" handling below).
  function tryRestoreImpersonation() {
    try {
      const raw = sessionStorage.getItem("divve_impersonation");
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.accessToken || !parsed?.user || new Date(parsed.expiresAt).getTime() <= Date.now()) {
        sessionStorage.removeItem("divve_impersonation");
        return null;
      }
      return parsed;
    } catch {
      return null;
    }
  }

  // Guards against React StrictMode's deliberate double-invoke of this effect
  // in development (mount -> cleanup -> mount again). Without this, TWO
  // concurrent POST /auth/refresh calls go out sharing the SAME refresh
  // cookie — the backend's single-use rotation treats the second one
  // presenting an already-consumed token as theft and revokes every live
  // token for the account (see authController.ts's refresh()). The first
  // call still wins and this page load renders logged in, so nothing looks
  // wrong yet — it's the refresh cookie's replacement that's silently dead,
  // which only surfaces as a hard logout on the NEXT reload. Mirrors the
  // identical guard already in admin/AdminAuthContext.jsx (restoreAttemptedRef)
  // — confirmed live here too: reload right after login looked fine, the very
  // next reload landed back on splash, logged out.
  const restoreAttemptedRef = useRef(false);

  // On mount: try to restore a session from the httpOnly refresh cookie.
  useEffect(() => {
    if (restoreAttemptedRef.current) return;
    restoreAttemptedRef.current = true;
    // A notification/email/pop-up link like `/?go=login` — captured before
    // anything else so it survives the async session check below.
    captureDeepLink();
    (async () => {
      const impersonation = tryRestoreImpersonation();
      try {
        const data = impersonation ? { accessToken: impersonation.accessToken, user: impersonation.user } : (await api.post("/auth/refresh")).data;
        setAccessToken(data.accessToken);
        setUser(data.user);
        setIsImpersonating(Boolean(impersonation));
        setPrefs((p) => ({
          ...p,
          ...(data.user.preferences || {}),
          // Backend stores these under different field names (diversificationGoal,
          // preferredCategories, excludedCategories) than the frontend's prefs
          // shape expects — the spread above only updates same-named fields
          // (risk, returnExpectation); without this, diversification priority
          // and preferred/excluded categories silently reset to defaults on
          // every reload/login despite having saved correctly.
          diversificationPriority: data.user.preferences?.diversificationGoal ?? p.diversificationPriority,
          preferred: data.user.preferences?.preferredCategories ?? p.preferred,
          excluded: data.user.preferences?.excludedCategories ?? p.excluded,
        }));
        skipNextPlannerSaveRef.current = true;
        loadPlannerStateFrom(data.user);
        await loadHoldings();
        // Always land on Home — even with zero holdings, it shows the
        // dashboard's own empty state plus a dismissible "get started" popup
        // (see Home.jsx's GetStartedPopup), rather than dropping the user
        // straight into the fetch-method chooser with no dashboard in sight.
        // The one exception: a members-only deep link (`/?go=subscription`).
        setScreen(screenAfterLogin());
      } catch (e) {
        // no valid session — stay on splash/onboarding, unless a deep link
        // (`/?go=login`, `/?go=signup`) asks for a specific one of them
        const target = screenWhenLoggedOut();
        if (target) setScreen(target);
      } finally {
        setAuthLoading(false);
      }
    })();
  }, [loadHoldings, setScreen]);

  // Registers the one global handler for "the refresh token is dead mid-
  // session" (lib/api.js's response interceptor calls this after its own
  // refresh attempt fails on a 401 from some other endpoint) — without this,
  // only the very first page-load session check could ever send a user back
  // to onboarding; every later expiry just left requests failing silently
  // against whatever screen was already open.
  useEffect(() => {
    setSessionExpiredHandler(() => {
      setAccessToken(null);
      setUser(null);
      setHoldings([]);
      setScreen("splash");
    });
  }, [setScreen]);

  // Phase 6a of docs/ADMIN_PANEL_PLAN.md §5.4/§7 — the user's own plan +
  // limits, refetched whenever the identity changes (login/logout/signup)
  // and after any subscription action (screens/Subscription.jsx calls
  // refreshEntitlements itself after subscribing/cancelling, since those
  // don't change `user`). `null` while unauthenticated or not yet loaded —
  // consumers should treat that as "unknown", not "Freemium".
  const [entitlements, setEntitlements] = useState(null);
  const refreshEntitlements = useCallback(async () => {
    try {
      const { data } = await api.get("/me/entitlements");
      setEntitlements(data);
    } catch (e) {
      // best-effort — a failed fetch just leaves paywall UI showing nothing
    }
  }, []);
  useEffect(() => {
    if (user) refreshEntitlements();
    else setEntitlements(null);
  }, [user, refreshEntitlements]);

  // The shared upgrade-prompt trigger — lib/api.js's response interceptor
  // calls this the instant ANY request hits PLAN_LIMIT_REACHED, wherever
  // that happened (bot scan, doc upload, a holding mutation), so DiveShell
  // can show one consistent modal instead of each feature building its own.
  const [planLimitInfo, setPlanLimitInfo] = useState(null);
  useEffect(() => {
    setPlanLimitHandler((info) => setPlanLimitInfo(info));
  }, []);

  const signupStart = async (form) => {
    const { data } = await api.post("/auth/signup/start", form);
    return data; // { message, mobile, devOtp? }
  };

  // The demo phone frame and the real logged-in app share this same
  // long-lived context (deliberately never remounted on login — see
  // App.js/PhoneFrame), so anything a logged-out visitor played with in the
  // demo (Ask DIVVE's "what-if" simulation — never persisted, unlike
  // plannerState now) would otherwise survive straight into their real
  // account the moment they sign up or log in, in the same tab. Planner
  // itself is handled by loadPlannerStateFrom (loads the new identity's real
  // saved value, or its defaults if it has none) rather than a blind reset.
  // Also matters for logout→a different person logging in on the same
  // shared/kiosk browser.
  const resetClientOnlyScratchState = () => {
    skipNextPlannerSaveRef.current = true;
    setPlannerStateRaw(DEFAULT_PLANNER_STATE);
    setSims([]);
  };

  const signupVerify = async (mobile, otp) => {
    const { data } = await api.post("/auth/signup/verify", { mobile, otp });
    setAccessToken(data.accessToken);
    setUser(data.user);
    skipNextPlannerSaveRef.current = true;
    loadPlannerStateFrom(data.user);
    setSims([]);
    return data.user;
  };

  const login = async (identifier, password) => {
    const { data } = await api.post("/auth/login", { identifier, password });
    // A staff account (backend/src/models/User.ts's staffRole) never gets a
    // real session from this endpoint alone — it gets a short-lived pending
    // token instead, pending a separate mandatory TOTP step (see
    // docs/ADMIN_PANEL_PLAN.md Phase 0.3/0.4). That flow lives entirely in
    // src/admin/ (a different login screen, at /admin) — surfacing a clear,
    // specific error here instead of silently trying (and failing) to treat
    // `data.user`/`data.accessToken` as real when neither exists on this
    // response shape.
    if (data.staffAuthRequired) {
      const err = new Error("This is a staff account — sign in at /admin instead.");
      err.staffAuthRequired = true;
      throw err;
    }
    setAccessToken(data.accessToken);
    setUser(data.user);
    setPrefs((p) => ({ ...p, ...(data.user.preferences || {}) }));
    skipNextPlannerSaveRef.current = true;
    loadPlannerStateFrom(data.user);
    setSims([]);
    await loadHoldings();
    // Always land on Home — see the matching comment on the session-restore
    // effect above for why (and for the members-only deep-link exception).
    setScreen(screenAfterLogin());
    return data.user;
  };

  // Forgot-password: three separate calls, mirroring the three backend steps
  // (send OTP -> verify OTP -> reset). None of these authenticate the user —
  // like changePassword, a reset just changes the stored password and routes
  // back to the normal login screen, rather than logging the user straight
  // in. Errors are left to the caller's own try/catch (same convention as
  // every other context function here) rather than swallowed here.
  const forgotPasswordStart = async (identifier) => {
    const { data } = await api.post("/auth/forgot-password/start", { identifier });
    return data; // { message, mobile, devOtp? }
  };

  const forgotPasswordVerify = async (mobile, otp) => {
    const { data } = await api.post("/auth/forgot-password/verify", { mobile, otp });
    return data.resetToken;
  };

  const resetPassword = async (resetToken, newPassword, confirmNewPassword) => {
    const { data } = await api.post("/auth/forgot-password/reset", { resetToken, newPassword, confirmNewPassword });
    return data.message;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* ignore */ }
    setAccessToken(null);
    setUser(null);
    setHoldings([]);
    resetClientOnlyScratchState();
    setScreen("splash");
  };

  // No real session to log out of (no refresh cookie exists for an
  // impersonation token at all — see tryRestoreImpersonation's own comment),
  // so this just discards the hand-off and returns to the splash screen.
  const exitImpersonation = () => {
    sessionStorage.removeItem("divve_impersonation");
    setAccessToken(null);
    setUser(null);
    setHoldings([]);
    setIsImpersonating(false);
    resetClientOnlyScratchState();
    setScreen("splash");
  };

  // Applies optimistically (instant UI feedback), but rolls back and reports
  // failure to the caller on error — previously this was a silent no-op on
  // failure, so the UI kept showing a "saved" change that never reached the
  // backend and would be quietly lost on the next real reload/login.
  const savePrefs = async (next) => {
    const previous = prefs;
    setPrefs(next);
    try {
      await api.patch("/users/me/preferences", next);
      return true;
    } catch (e) {
      setPrefs(previous);
      return false;
    }
  };

  const deleteHolding = async (holdingId) => {
    await api.delete(`/holdings/${holdingId}`);
    await loadHoldings();
  };

  const updateHolding = async (holdingId, payload) => {
    await api.patch(`/holdings/${holdingId}`, payload);
    await loadHoldings();
  };

  const updateProfile = async (payload) => {
    const { data } = await api.patch("/users/me/profile", payload);
    setUser(data.user);
    // Age drives the backend's persona/life-stage context (contextEngine.ts),
    // which scoreBreakdown.context carries into Suggestions/Home's messaging
    // — without this, a changed age wouldn't show up there until some other
    // action (adding/deleting a holding) happened to refetch scoreBreakdown,
    // or the page was reloaded.
    if (holdings.length) loadScoreBreakdown();
    return data.user;
  };

  // Only ever flips one way (see backend/src/controllers/userController.ts's
  // markWalkthroughSeen) — called on both finishing AND skipping the tour,
  // since either one means "don't auto-start this again."
  const markWalkthroughSeen = async () => {
    const { data } = await api.patch("/users/me/walkthrough");
    setUser(data.user);
  };

  // Unlike savePrefs/updateProfile, this is never optimistic — there's
  // nothing locally cached to update either way, and a wrong current-password
  // guess needs to surface as a real error, not something to silently retry.
  const changePassword = async (payload) => {
    const { data } = await api.patch("/users/me/password", payload);
    return data.message;
  };

  const deleteAccount = async () => {
    await api.delete("/users/me");
    setAccessToken(null);
    setUser(null);
    setHoldings([]);
    setScreen("splash");
  };

  const value = {
    screen, setScreen, goBack, authLoading, user,
    signupStart, signupVerify, login, logout, deleteAccount,
    forgotPasswordStart, forgotPasswordVerify, resetPassword,
    updateProfile, changePassword,
    walkthroughOpen, setWalkthroughOpen, markWalkthroughSeen,
    holdings, holdingsLoading, holdingsError, loadHoldings, deleteHolding, updateHolding,
    scoreBreakdown, loadScoreBreakdown,
    ranges, prefs, setPrefs, savePrefs,
    askInstrument, setAskInstrument,
    editingHolding, setEditingHolding,
    sims, addSim, resetSims,
    plannerState, setPlannerState,
    entitlements, refreshEntitlements,
    planLimitInfo, setPlanLimitInfo,
    isImpersonating, exitImpersonation,
  };
  return <DiveContext.Provider value={value}>{children}</DiveContext.Provider>;
}
